import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

import "./themeupdate.css";

import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import StageRulesEditor, {
  normalizeRulesFromTheme,
  STAGE_HEADING_MAX_LEN,
  validateStageHeadingAndRules,
} from "./StageRulesEditor";
import PopupTab, { POPUP_TEXT_MAX_LEN } from "./PopupTab";
import { uploadPopupImage } from "./popupImageUpload";
import {
  getUnscrambleMinAnswersForAdmin,
  parseShowClue,
} from "../../../pages/game/unscrambleUtils";

const UNSCRAMBLE_MAX_PAIRS = 12;
const UNSCRAMBLE_MIN_PAIRS = 2;
const UNSCRAMBLE_QA_MAX_LEN = 100;
const UNSCRAMBLE_MIN_ANSWERS_MAX = UNSCRAMBLE_MAX_PAIRS;
const CLUE_MAX_LEN = 100;

const clampStageHeading = (raw) => String(raw ?? "").slice(0, STAGE_HEADING_MAX_LEN);

let pairIdCounter = 0;
function newPairId(prefix = "pair") {
  pairIdCounter += 1;
  return `${prefix}-${pairIdCounter}`;
}

function sanitizeAlphaNum(value) {
  return String(value ?? "").replace(/[^a-zA-Z0-9\s]/g, "");
}

/** Letters/digits only, lowercased — for unscramble char comparison. */
function unscrambleCharsOnly(value) {
  return String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/**
 * True when every answer character (ignoring spaces/case) appears in the
 * question with at least the same frequency — i.e. answer uses only chars
 * from the scrambled question text.
 */
function answerCharsBelongToQuestion(question, answer) {
  const qChars = unscrambleCharsOnly(question);
  const aChars = unscrambleCharsOnly(answer);
  if (!aChars) return true;

  const pool = {};
  for (const ch of qChars) {
    pool[ch] = (pool[ch] || 0) + 1;
  }
  for (const ch of aChars) {
    if (!pool[ch]) return false;
    pool[ch] -= 1;
  }
  return true;
}

function emptyUnscramblePair() {
  return { id: newPairId("un"), question: "", answer: "", clue: "" };
}

function normalizeUnscrambleFromTheme(raw) {
  const list = Array.isArray(raw) ? raw : [];
  if (!list.length) {
    return [emptyUnscramblePair(), emptyUnscramblePair()];
  }
  return list.map((item) => ({
    id: newPairId("un"),
    question: sanitizeAlphaNum(item?.question).slice(0, UNSCRAMBLE_QA_MAX_LEN),
    answer: sanitizeAlphaNum(item?.answer).slice(0, UNSCRAMBLE_QA_MAX_LEN),
    clue: String(item?.clue ?? "").replace(/^\s+/, "").slice(0, CLUE_MAX_LEN),
  }));
}

function UnscrambleTab({
  pairs,
  setPairs,
  minAnswersRequired,
  setMinAnswersRequired,
  isSaving,
  onSavePairs,
  onSaveSettings,
  errorMessage,
  successMessage,
  setErrorMessage,
  setSuccessMessage,
  showClue,
  onToggleShowClue,
}) {
  const addPair = () => {
    if (pairs.length >= UNSCRAMBLE_MAX_PAIRS) {
      setErrorMessage(`You can add up to ${UNSCRAMBLE_MAX_PAIRS} pairs.`);
      return;
    }
    setErrorMessage("");
    setPairs((prev) => [...prev, emptyUnscramblePair()]);
  };

  const removePair = (id) => {
    if (pairs.length <= UNSCRAMBLE_MIN_PAIRS) {
      setErrorMessage(`You need at least ${UNSCRAMBLE_MIN_PAIRS} pairs.`);
      return;
    }
    setPairs((prev) => prev.filter((p) => p.id !== id));
    setErrorMessage("");
  };

  const updatePair = (id, field, value) => {
    const sanitized =
      field === "clue"
        ? String(value ?? "").replace(/^\s+/, "").slice(0, CLUE_MAX_LEN)
        : sanitizeAlphaNum(value).slice(0, UNSCRAMBLE_QA_MAX_LEN);
    setPairs((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: sanitized } : p))
    );
    setErrorMessage("");
    setSuccessMessage("");
  };

  const clearAll = () => {
    setPairs([emptyUnscramblePair(), emptyUnscramblePair()]);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleMinAnswersChange = (value) => {
    const raw = String(value ?? "").replace(/\D/g, "");
    if (!raw) {
      setMinAnswersRequired("");
      return;
    }
    const n = Math.min(UNSCRAMBLE_MIN_ANSWERS_MAX, Math.max(1, Math.trunc(Number(raw) || 1)));
    setMinAnswersRequired(String(n));
  };

  const blockNonDigitMinAnswersKey = (e) => {
    // Block letters (e/E), signs, decimals, and other non-digit keys that type=number allows.
    if (
      e.key.length === 1 &&
      !/\d/.test(e.key) &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.altKey
    ) {
      e.preventDefault();
    }
  };

  const leftPairs = pairs.filter((_, i) => i % 2 === 0);
  const rightPairs = pairs.filter((_, i) => i % 2 === 1);

  const renderPairCard = (pair) => (
    <div
      key={pair.id}
      className={`qa-pair-card${showClue ? " qa-pair-card--with-clue" : ""}`}
    >
      <label htmlFor={`uq-${pair.id}`}>Question</label>
      <input
        id={`uq-${pair.id}`}
        type="text"
        value={pair.question}
        maxLength={UNSCRAMBLE_QA_MAX_LEN}
        onChange={(e) => updatePair(pair.id, "question", e.target.value)}
        placeholder="Scrambled word or phrase"
      />
      <button
        type="button"
        className="qa-pair-delete"
        onClick={() => removePair(pair.id)}
        aria-label="Delete pair"
        title="Delete pair"
      >
        <i className="fa-regular fa-trash-can" />
      </button>
      <label htmlFor={`ua-${pair.id}`}>Answer</label>
      <input
        id={`ua-${pair.id}`}
        type="text"
        value={pair.answer}
        maxLength={UNSCRAMBLE_QA_MAX_LEN}
        onChange={(e) => updatePair(pair.id, "answer", e.target.value)}
        placeholder="Correct answer"
      />
      {showClue ? (
        <>
          <label htmlFor={`uc-${pair.id}`}>Clue</label>
          <input
            id={`uc-${pair.id}`}
            type="text"
            value={pair.clue}
            maxLength={CLUE_MAX_LEN}
            onChange={(e) => updatePair(pair.id, "clue", e.target.value)}
            placeholder="Optional hint shown to players"
          />
        </>
      ) : null}
    </div>
  );

  return (
    <div className="themeupdate-panel-wide unscramble-panel">
      <p className="qa-char-limit-hint">
        * Add {UNSCRAMBLE_MIN_PAIRS}–{UNSCRAMBLE_MAX_PAIRS} complete pairs. Letters, numbers, and
        spaces only. Question and answer — {UNSCRAMBLE_QA_MAX_LEN} characters each. Answer must
        use only characters from the question (same letters/numbers, ignoring spaces and case).
      </p>

      {(errorMessage || successMessage) && (
        <div className="qa-alerts">
          {errorMessage ? <p className="cr-alert cr-alert-error">{errorMessage}</p> : null}
          {successMessage ? <p className="cr-alert cr-alert-success">{successMessage}</p> : null}
        </div>
      )}

      <div className="unscramble-show-clue-row">
        <label className="unscramble-toggle">
          <input
            type="checkbox"
            checked={showClue}
            onChange={(e) => onToggleShowClue(e.target.checked)}
          />
          <span className="unscramble-toggle-slider" />
        </label>
        <label className="unscramble-show-clue-label">Show Clue</label>
      </div>

      <div className="qa-pairs-columns">
        <div className="qa-pairs-col">{leftPairs.map(renderPairCard)}</div>
        <div className="qa-pairs-col">{rightPairs.map(renderPairCard)}</div>
      </div>

      <div className="qa-pairs-actions">
        <button
          type="button"
          className="qa-btn qa-btn-outline"
          onClick={addPair}
          disabled={pairs.length >= UNSCRAMBLE_MAX_PAIRS}
        >
          Add Pair
        </button>
        <button
          type="button"
          className="qa-btn qa-btn-primary"
          onClick={onSavePairs}
          disabled={isSaving}
        >
          {isSaving ? "Saving…" : "Save and Continue"}
        </button>
        <button type="button" className="qa-btn qa-btn-primary" onClick={clearAll}>
          Clear All
        </button>
      </div>

      <div className="unscramble-min-answers">
        <label htmlFor="unscramble-min-answers">Minimum answers required for submit</label>
        <input
          id="unscramble-min-answers"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          value={minAnswersRequired}
          onChange={(e) => handleMinAnswersChange(e.target.value)}
          onKeyDown={blockNonDigitMinAnswersKey}
          onPaste={(e) => {
            e.preventDefault();
            handleMinAnswersChange(e.clipboardData.getData("text"));
          }}
        />
      </div>

      <div className="unscramble-save-bottom">
        <button
          type="button"
          className="qa-btn qa-btn-primary"
          onClick={onSaveSettings}
          disabled={isSaving}
        >
          {isSaving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

function StageHeadingEditor({
  stageLabel,
  heading,
  setHeading,
  onSave,
  isSaving = false,
  saveLabel = "Save",
}) {
  return (
    <div className="themeupdate-panel-wide">
      <div className="themeupdate-card">
        <div className="themeupdate-card-body">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            <div style={{ flex: "1 1 320px", minWidth: 280 }}>
              <label className="themeupdate-label">{stageLabel} heading</label>
              <input
                type="text"
                className="themeupdate-input"
                value={heading}
                maxLength={STAGE_HEADING_MAX_LEN}
                onChange={(e) => setHeading(clampStageHeading(e.target.value))}
              />
            </div>
          </div>

          <p className="themeupdate-muted" style={{ marginTop: 8 }}>
            * Heading — max {STAGE_HEADING_MAX_LEN} characters.
          </p>

          {onSave ? (
            <div className="stage-heading-save-wrap">
              <button
                type="button"
                className="qa-btn qa-btn-primary"
                onClick={onSave}
                disabled={isSaving}
              >
                {isSaving ? "Saving…" : saveLabel}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const ThemeUpdate = () => {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const navigate = useNavigate();
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [unscramblePairs, setUnscramblePairs] = useState([emptyUnscramblePair(), emptyUnscramblePair()]);
  const [minAnswersRequired, setMinAnswersRequired] = useState("1");
  const [isSaving, setIsSaving] = useState(false);
  const [unscrambleErrorMessage, setUnscrambleErrorMessage] = useState("");
  const [unscrambleSuccessMessage, setUnscrambleSuccessMessage] = useState("");

  // Stage heading (admin-configurable per mini-game stage).
  const [unscrambleHeading, setUnscrambleHeading] = useState("");
  const [unscrambleRules, setUnscrambleRules] = useState([""]);
  const [showClue, setShowClue] = useState(false);

  const [activeTab, setActiveTab] = useState("unscramble");

  // Correct answer / end game popups (text or image, mutually exclusive).
  const [correctAnsText, setCorrectAnsText] = useState("");
  const [correctAnsImage, setCorrectAnsImage] = useState("");
  const [endGameText, setEndGameText] = useState("");
  const [endGameImage, setEndGameImage] = useState("");
  const [popupErrorMessage, setPopupErrorMessage] = useState("");
  const [popupSuccessMessage, setPopupSuccessMessage] = useState("");
  const [isSavingCorrectPopup, setIsSavingCorrectPopup] = useState(false);
  const [isSavingEndGamePopup, setIsSavingEndGamePopup] = useState(false);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setUnscramblePairs(normalizeUnscrambleFromTheme(data.unscramble));
    const minWords = getUnscrambleMinAnswersForAdmin(data);
    setMinAnswersRequired(
      String(Math.min(UNSCRAMBLE_MIN_ANSWERS_MAX, minWords))
    );

    const titleFallback = String(
      data.landing_page_title ?? data.main_title ?? data.themename ?? ""
    ).trim();

    setUnscrambleHeading(
      clampStageHeading(data.unscramble_title ?? titleFallback ?? "Unscramble")
    );

    setUnscrambleRules(normalizeRulesFromTheme(data.unscramble_rules));
    setShowClue(parseShowClue(data));

    setCorrectAnsText(String(data.Correct_ans_text_popup ?? "").trim());
    setCorrectAnsImage(String(data.puzzle_image ?? "").trim());
    setEndGameText(String(data.end_game_text_popup ?? "").trim());
    setEndGameImage(String(data.end_game_img_popup ?? "").trim());
  }, [data]);

  const validateUnscramblePairs = () => {
    const filled = unscramblePairs
      .map((p) => ({
        question: sanitizeAlphaNum(p.question).trim(),
        answer: sanitizeAlphaNum(p.answer).trim(),
        clue: String(p.clue ?? "").trim(),
      }))
      .filter((p) => p.question || p.answer);

    const complete = filled.filter((p) => p.question && p.answer);

    if (complete.length < UNSCRAMBLE_MIN_PAIRS) {
      return {
        ok: false,
        message: `Please add at least ${UNSCRAMBLE_MIN_PAIRS} complete question/answer pairs before saving.`,
      };
    }

    if (complete.length > UNSCRAMBLE_MAX_PAIRS) {
      return {
        ok: false,
        message: `You can save up to ${UNSCRAMBLE_MAX_PAIRS} question/answer pairs.`,
      };
    }

    const hasEmpty = filled.some((p) => !p.question || !p.answer);
    if (hasEmpty) {
      return { ok: false, message: "Please fill in all fields or remove incomplete pairs." };
    }

    const tooLongQa = complete.find(
      (p) => p.question.length > UNSCRAMBLE_QA_MAX_LEN || p.answer.length > UNSCRAMBLE_QA_MAX_LEN
    );
    if (tooLongQa) {
      return {
        ok: false,
        message: `Each question and answer must be ${UNSCRAMBLE_QA_MAX_LEN} characters or fewer.`,
      };
    }

    const mismatched = complete.findIndex(
      (p) => !answerCharsBelongToQuestion(p.question, p.answer)
    );
    if (mismatched !== -1) {
      return {
        ok: false,
        message: `Pair ${mismatched + 1}: answer contains characters that are not in the question. The answer must use only letters/numbers from the scrambled question text.`,
      };
    }

    return {
      ok: true,
      complete: complete.map((p) => ({
        question: p.question,
        answer: p.answer,
        clue: p.clue,
      })),
    };
  };

  const handleSaveUnscramblePairs = async () => {
    const stageMeta = validateStageHeadingAndRules(
      unscrambleHeading,
      unscrambleRules,
      "Unscramble"
    );
    if (!stageMeta.ok) {
      setUnscrambleErrorMessage(stageMeta.message);
      setUnscrambleSuccessMessage("");
      return;
    }

    const validation = validateUnscramblePairs();
    if (!validation.ok) {
      setUnscrambleErrorMessage(validation.message);
      setUnscrambleSuccessMessage("");
      return;
    }

    setIsSaving(true);
    setUnscrambleErrorMessage("");
    setUnscrambleSuccessMessage("");

    try {
      await updateThemeData({
        payload: {
          data: {
            unscramble: validation.complete,
            unscramble_title: stageMeta.heading,
            unscramble_rules: stageMeta.rules,
            question_count: validation.complete.length,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      setUnscramblePairs(
        validation.complete.map((p) => ({
          id: newPairId("un"),
          question: p.question,
          answer: p.answer,
          clue: p.clue,
        }))
      );
      Swal.fire({
        title: "Success",
        text: "Unscramble words saved to theme.",
        icon: "success",
        timer: 5000,
        timerProgressBar: false,
        customClass: {
          popup: "themeupdate-alert-popup",
          container: "themeupdate-alert-container",
        },
      });
      setUnscrambleSuccessMessage("Pairs saved to theme.");
    } catch (err) {
      setUnscrambleErrorMessage(err?.message || "Failed to save data.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveUnscrambleSettings = async () => {
    const stageMeta = validateStageHeadingAndRules(
      unscrambleHeading,
      unscrambleRules,
      "Unscramble"
    );
    if (!stageMeta.ok) {
      setUnscrambleErrorMessage(stageMeta.message);
      setUnscrambleSuccessMessage("");
      return;
    }

    const validation = validateUnscramblePairs();
    if (!validation.ok) {
      setUnscrambleErrorMessage(validation.message);
      setUnscrambleSuccessMessage("");
      return;
    }

    const minVal = Math.trunc(Number(minAnswersRequired) || 0);
    if (!Number.isFinite(minVal) || minVal < 1) {
      setUnscrambleErrorMessage("Minimum answers required must be at least 1.");
      setUnscrambleSuccessMessage("");
      return;
    }

    if (minVal > validation.complete.length) {
      setUnscrambleErrorMessage(
        `Minimum answers cannot be more than the number of pairs (${validation.complete.length}).`
      );
      setUnscrambleSuccessMessage("");
      return;
    }

    if (minVal > UNSCRAMBLE_MIN_ANSWERS_MAX) {
      setUnscrambleErrorMessage(`Minimum answers cannot exceed ${UNSCRAMBLE_MIN_ANSWERS_MAX}.`);
      return;
    }

    setIsSaving(true);
    setUnscrambleErrorMessage("");
    setUnscrambleSuccessMessage("");

    try {
      await updateThemeData({
        payload: {
          data: {
            words: minVal,
            unscramble_min_answers: minVal,
            unscramble_title: stageMeta.heading,
            unscramble_rules: stageMeta.rules,
          },
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      Swal.fire({
        title: "Success",
        text: "Unscramble settings saved.",
        icon: "success",
        timer: 5000,
        timerProgressBar: false,
        customClass: {
          popup: "themeupdate-alert-popup",
          container: "themeupdate-alert-container",
        },
      });
      setUnscrambleSuccessMessage("Settings saved.");
    } catch (err) {
      setUnscrambleErrorMessage(err?.message || "Failed to save settings.");
    } finally {
      setIsSaving(false);
    }
  };

  const saveThemeFields = async (fields) => {
    await updateThemeData({
      payload: {
        data: fields,
        organizationId: organizationId || "admin",
        sessionId: sessionId || "admin",
        currentTheme,
      },
      token: adminToken,
    });
    await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
  };

  const handleToggleShowClue = async (checked) => {
    const previous = showClue;
    setShowClue(checked);
    try {
      await saveThemeFields({ show_clue: checked ? "true" : "false" });
    } catch (err) {
      setShowClue(previous);
      Swal.fire({
        icon: "error",
        text: err?.message || "Failed to update Show Clue setting.",
        customClass: {
          popup: "themeupdate-alert-popup",
          container: "themeupdate-alert-container",
        },
      });
    }
  };

  const handleSubmitCorrectPopup = async ({ text, file }) => {
    if (text.length > POPUP_TEXT_MAX_LEN) {
      setPopupErrorMessage(
        `Correct answer popup text must be at most ${POPUP_TEXT_MAX_LEN} characters.`
      );
      return;
    }
    setPopupErrorMessage("");
    setPopupSuccessMessage("");
    setIsSavingCorrectPopup(true);
    try {
      if (file) {
        const filename = await uploadPopupImage({
          file,
          adminToken,
          currentTheme,
          fieldKey: "puzzle_image",
        });
        await saveThemeFields({ Correct_ans_text_popup: "" });
        setCorrectAnsImage(filename);
        setCorrectAnsText("");
      } else {
        await saveThemeFields({ Correct_ans_text_popup: text, puzzle_image: "" });
        setCorrectAnsText(text);
        setCorrectAnsImage("");
      }
      setPopupSuccessMessage("Correct answer popup saved.");
    } catch (err) {
      Swal.fire({
        icon: "error",
        text: err?.message || "Failed to save popup.",
        customClass: {
          popup: "themeupdate-alert-popup",
          container: "themeupdate-alert-container",
        },
      });
    } finally {
      setIsSavingCorrectPopup(false);
    }
  };

  const handleSubmitEndGamePopup = async ({ text, file }) => {
    if (text.length > POPUP_TEXT_MAX_LEN) {
      setPopupErrorMessage(
        `End game popup text must be at most ${POPUP_TEXT_MAX_LEN} characters.`
      );
      return;
    }
    setPopupErrorMessage("");
    setPopupSuccessMessage("");
    setIsSavingEndGamePopup(true);
    try {
      if (file) {
        const filename = await uploadPopupImage({
          file,
          adminToken,
          currentTheme,
          fieldKey: "end_game_img_popup",
        });
        await saveThemeFields({ end_game_text_popup: "" });
        setEndGameImage(filename);
        setEndGameText("");
      } else {
        await saveThemeFields({ end_game_text_popup: text, end_game_img_popup: "" });
        setEndGameText(text);
        setEndGameImage("");
      }
      setPopupSuccessMessage("End game popup saved.");
    } catch (err) {
      Swal.fire({
        icon: "error",
        text: err?.message || "Failed to save popup.",
        customClass: {
          popup: "themeupdate-alert-popup",
          container: "themeupdate-alert-container",
        },
      });
    } finally {
      setIsSavingEndGamePopup(false);
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="themeupdate-page">
        <div className="themeupdate-loading">Loading theme data…</div>
      </div>
    );
  }

  return (
    <div className="themeupdate-page">
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <header className="themeupdate-header themeupdate-panel-wide">
        <h1 className="themeupdate-title">Game Content</h1>
        <p className="themeupdate-muted">Manage questions and content for the Unscramble mini-game in this theme.</p>
      </header>

      <div className="themeupdate-tabs">
        <button
          type="button"
          className={`themeupdate-tab${activeTab === "unscramble" ? " active" : ""}`}
          onClick={() => setActiveTab("unscramble")}
        >
          <i className="fa fa-plus-square" aria-hidden /> Create Unscramble
        </button>
        <button
          type="button"
          className={`themeupdate-tab${activeTab === "popup" ? " active" : ""}`}
          onClick={() => setActiveTab("popup")}
        >
          <i className="fa fa-plus-square" aria-hidden /> Add Custom Pop Up
        </button>
      </div>

      {activeTab === "unscramble" ? (
        <>
          <StageHeadingEditor
            stageLabel="Stage"
            heading={unscrambleHeading}
            setHeading={setUnscrambleHeading}
          />
          <StageRulesEditor
            stageLabel="Stage"
            rules={unscrambleRules}
            setRules={setUnscrambleRules}
          />
          <UnscrambleTab
            pairs={unscramblePairs}
            setPairs={setUnscramblePairs}
            minAnswersRequired={minAnswersRequired}
            setMinAnswersRequired={setMinAnswersRequired}
            isSaving={isSaving}
            onSavePairs={handleSaveUnscramblePairs}
            onSaveSettings={handleSaveUnscrambleSettings}
            errorMessage={unscrambleErrorMessage}
            successMessage={unscrambleSuccessMessage}
            setErrorMessage={setUnscrambleErrorMessage}
            setSuccessMessage={setUnscrambleSuccessMessage}
            showClue={showClue}
            onToggleShowClue={handleToggleShowClue}
          />
        </>
      ) : (
        <PopupTab
          correctAnsText={correctAnsText}
          correctAnsImage={correctAnsImage}
          endGameText={endGameText}
          endGameImage={endGameImage}
          onSubmitCorrect={handleSubmitCorrectPopup}
          onSubmitEndGame={handleSubmitEndGamePopup}
          isSavingCorrect={isSavingCorrectPopup}
          isSavingEndGame={isSavingEndGamePopup}
          errorMessage={popupErrorMessage}
          successMessage={popupSuccessMessage}
        />
      )}
    </div>
  );
};

export default ThemeUpdate;
